import { useEffect, type RefObject } from 'react';
import { apiClient } from '../lib/apiClient';

const REPORT_EVERY_MS = 15000;
/** Larger jumps between two time updates are seeks, not playing. */
const MAX_PLAYED_STEP_S = 2;

/**
 * Viewer insights: reports when playback starts and how many distinct seconds were actually
 * played (skipped parts don't count, rewatched parts count once) — every 15 s while playing,
 * on pause / end, and when the page is hidden or closed. The server counts one view per viewer
 * per day and ignores the owner.
 */
export function useWatchTracking(videoRef: RefObject<HTMLVideoElement>, videoId: string | undefined, enabled: boolean) {
  useEffect(() => {
    const video = videoRef.current;
    if (!enabled || !videoId || !video) return;

    let started = false;
    // Whole seconds of the video that were played (not skipped over).
    const watched = new Set<number>();
    let reported = -1;
    let last = video.currentTime;
    let timer: ReturnType<typeof setInterval> | undefined;

    const duration = () => (Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0);
    const send = (keepalive = false) => {
      const position = watched.size;
      if (started && position <= reported) return;
      const start = !started; // the first report of this player is the play start (counts the view)
      started = true;
      reported = position;
      void apiClient.reportWatch(videoId, position, Math.round(duration()), keepalive, start);
    };

    const onPlay = () => {
      send();
      timer ??= setInterval(() => send(), REPORT_EVERY_MS);
    };
    const onTimeUpdate = () => {
      const t = video.currentTime;
      if (t > last && t - last < MAX_PLAYED_STEP_S) {
        for (let s = Math.floor(last); s <= Math.floor(t); s++) watched.add(s);
      }
      last = t;
    };
    const onSeeked = () => { last = video.currentTime; };
    const onStop = () => {
      clearInterval(timer);
      timer = undefined;
      if (started) send();
    };
    // The page may be closed without a pause: send what was watched (keepalive outlives the page).
    const onHidden = () => { if (document.visibilityState === 'hidden' && started) send(true); };

    video.addEventListener('play', onPlay);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('pause', onStop);
    video.addEventListener('ended', onStop);
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', onHidden);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('pause', onStop);
      video.removeEventListener('ended', onStop);
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onHidden);
      clearInterval(timer);
      if (started) send(true); // player closed
    };
  }, [videoRef, videoId, enabled]);
}
